/**
 * PIK Mandatory Line Items
 *
 * Manages mandatory cart line items (e.g., Circular Flange Labor) that are
 * paired with a parent BW liner via _pik_config_id. Responsibilities:
 *
 * 1. Hide remove button on mandatory items
 * 2. Hide quantity selector on mandatory items
 * 3. Sync parent liner quantity changes / removals to paired mandatory items
 * 4. Re-run after Shopify section renders and AJAX cart updates
 *
 * All cart state reads use /cart.js and all updates use /cart/update.js with
 * exact line item keys — never variant ID — to avoid mis-targeting when
 * duplicate variants exist.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Constants
  // ---------------------------------------------------------------------------

  var PROCESSED_ATTR = 'data-pik-mli-processed';
  var CART_CONTAINERS = [
    '[data-section-type="cart"]',
    '.mini-cart',
    '.cart-drawer',
    '.cart__items',
    '.cart-items',
  ];

  // Debounce timer
  var runTimeout = null;

  // Guard against concurrent runs
  var running = false;

  // ---------------------------------------------------------------------------
  // Cart API helpers
  // ---------------------------------------------------------------------------

  /**
   * Fetch current cart state from Shopify.
   * Returns the full cart object with items array.
   */
  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then(function (r) {
      return r.json();
    });
  }

  /**
   * Batch update cart items by line key.
   * @param {Object} updates - { lineKey: quantity, ... }
   */
  function batchUpdate(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      return r.json();
    });
  }

  // ---------------------------------------------------------------------------
  // DOM helpers
  // ---------------------------------------------------------------------------

  /**
   * Find all cart line item elements in the DOM.
   * Warehouse theme uses [data-line] on quantity-related elements.
   * We look for the line item wrapper elements.
   */
  function findLineItemElements() {
    var items = [];
    // Warehouse cart page: .cart__item or tr with data attributes
    // Warehouse mini-cart: .mini-cart__line-item
    var selectors = [
      'tr.line-item',
      '.mini-cart__line-item',
      '.cart__item',
      '.cart-item',
      '[data-cart-item]',
      'tr[data-variant-id]',
    ];

    for (var s = 0; s < selectors.length; s++) {
      var found = document.querySelectorAll(selectors[s]);
      if (found.length > 0) {
        items = found;
        break;
      }
    }
    return items;
  }

  /**
   * Hide the remove button within a line item element.
   */
  function hideRemoveButton(el) {
    var removeSelectors = [
      '[data-action="remove-item"]',
      '.mini-cart__remove-item',
      '.cart__remove-item',
      '.cart-item__remove',
      'a[href*="/cart/change"]',
      'button[data-quantity="0"]',
    ];
    for (var i = 0; i < removeSelectors.length; i++) {
      var btns = el.querySelectorAll(removeSelectors[i]);
      for (var j = 0; j < btns.length; j++) {
        btns[j].style.display = 'none';
        btns[j].setAttribute(PROCESSED_ATTR, 'true');
      }
    }
  }

  /**
   * Hide/disable quantity controls within a line item element.
   */
  function hideQuantityControls(el) {
    var qtySelectors = [
      '.quantity-selector',
      '.cart-item__quantity',
      '.mini-cart__quantity',
      '[data-quantity-selector]',
    ];
    for (var i = 0; i < qtySelectors.length; i++) {
      var controls = el.querySelectorAll(qtySelectors[i]);
      for (var j = 0; j < controls.length; j++) {
        controls[j].style.display = 'none';
        controls[j].setAttribute(PROCESSED_ATTR, 'true');
      }
    }
    // Also disable any standalone quantity inputs
    var inputs = el.querySelectorAll('input[name="quantity"], input[type="number"]');
    for (var k = 0; k < inputs.length; k++) {
      inputs[k].disabled = true;
      inputs[k].setAttribute(PROCESSED_ATTR, 'true');
    }
  }

  // ---------------------------------------------------------------------------
  // Core logic
  // ---------------------------------------------------------------------------

  /**
   * Build a map from cart items: { configId: { parentKey, parentLine, mandatoryKey, mandatoryLine } }
   * Uses _pik_config_id and _pik_mandatory properties from /cart.js
   */
  function buildConfigMap(cartItems) {
    var map = {};

    for (var i = 0; i < cartItems.length; i++) {
      var item = cartItems[i];
      var props = item.properties || {};
      var configId = props._pik_config_id;
      if (!configId) continue;

      if (!map[configId]) {
        map[configId] = { parentKey: null, parentLine: null, mandatoryKey: null, mandatoryLine: null };
      }

      if (props._pik_mandatory === 'true') {
        map[configId].mandatoryKey = item.key;
        map[configId].mandatoryLine = i + 1; // 1-indexed
      } else {
        map[configId].parentKey = item.key;
        map[configId].parentLine = i + 1; // 1-indexed
      }
    }

    return map;
  }

  /**
   * Apply UI hiding to mandatory line item DOM elements.
   * Matches DOM elements to cart items by position (1-indexed data-line).
   */
  function applyHiding(configMap, lineItemElements) {
    var mandatoryLines = {};
    for (var id in configMap) {
      if (configMap[id].mandatoryLine) {
        mandatoryLines[configMap[id].mandatoryLine] = true;
      }
    }

    for (var i = 0; i < lineItemElements.length; i++) {
      var el = lineItemElements[i];
      var lineIndex = i + 1; // 1-indexed, matching Shopify's cart item order

      // Check by Liquid data attribute (if Step 4 Liquid changes are deployed)
      var isMandatory =
        el.getAttribute('data-pik-mandatory') === 'true' || mandatoryLines[lineIndex];

      if (isMandatory) {
        hideRemoveButton(el);
        hideQuantityControls(el);
        el.setAttribute(PROCESSED_ATTR, 'true');
      }
    }
  }

  /**
   * Intercept quantity change and removal events on parent liner items.
   * Batches both parent and mandatory item updates into a single /cart/update.js call.
   */
  function attachQuantityInterceptors(configMap, lineItemElements) {
    for (var id in configMap) {
      var entry = configMap[id];
      if (!entry.parentLine || !entry.mandatoryKey || !entry.parentKey) continue;

      var parentIndex = entry.parentLine - 1; // 0-indexed for DOM array
      var parentEl = lineItemElements[parentIndex];
      if (!parentEl || parentEl.getAttribute('data-pik-mli-intercepted')) continue;

      parentEl.setAttribute('data-pik-mli-intercepted', 'true');
      attachInterceptor(parentEl, entry);
    }
  }

  /**
   * Attach event interceptor to a single parent liner element.
   */
  function attachInterceptor(parentEl, entry) {
    // Intercept quantity input changes
    parentEl.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target || !target.matches('input[name="quantity"], .quantity-selector__value')) return;

        var newQty = parseInt(target.value, 10);
        if (isNaN(newQty) || newQty < 0) return;

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        syncQuantity(entry.parentKey, entry.mandatoryKey, newQty);
      },
      true
    );

    // Intercept +/- buttons and remove buttons
    parentEl.addEventListener(
      'click',
      function (e) {
        var target = e.target;
        if (!target) return;

        // Find closest clickable element
        var btn = target.closest('[data-quantity], [data-action="remove-item"], .cart__remove-item');
        if (!btn) return;

        var dataQty = btn.getAttribute('data-quantity');
        var isRemove =
          btn.getAttribute('data-action') === 'remove-item' ||
          btn.classList.contains('cart__remove-item') ||
          dataQty === '0';

        if (isRemove) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          syncQuantity(entry.parentKey, entry.mandatoryKey, 0);
          return;
        }

        if (dataQty !== null) {
          var qty = parseInt(dataQty, 10);
          if (!isNaN(qty)) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            syncQuantity(entry.parentKey, entry.mandatoryKey, qty);
          }
        }
      },
      true
    );
  }

  /**
   * Perform a batched quantity sync for parent + mandatory item.
   * Fetches fresh cart state to resolve current line keys, then sends
   * a single /cart/update.js call.
   */
  function syncQuantity(parentKey, mandatoryKey, newQty) {
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    // Fetch fresh cart to get current keys (they may shift after other operations)
    fetchCart()
      .then(function (cart) {
        // Re-resolve keys by _pik_config_id match
        var freshParentKey = null;
        var freshMandatoryKey = null;

        // Find the config ID from the parent key
        var configId = null;
        for (var i = 0; i < cart.items.length; i++) {
          if (cart.items[i].key === parentKey) {
            configId = (cart.items[i].properties || {})._pik_config_id;
            freshParentKey = cart.items[i].key;
            break;
          }
        }

        if (configId) {
          for (var j = 0; j < cart.items.length; j++) {
            var props = cart.items[j].properties || {};
            if (props._pik_config_id === configId && props._pik_mandatory === 'true') {
              freshMandatoryKey = cart.items[j].key;
              break;
            }
          }
        }

        // Build updates object using line keys
        var updates = {};
        if (freshParentKey) updates[freshParentKey] = newQty;
        if (freshMandatoryKey) updates[freshMandatoryKey] = newQty;

        if (Object.keys(updates).length === 0) {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
          return;
        }

        return batchUpdate(updates);
      })
      .then(function () {
        // Trigger theme cart refresh
        if (window.theme && window.theme.pageType === 'cart') {
          window.location.reload();
        } else {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
              detail: { scrollToTop: false },
            })
          );
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      })
      .catch(function (err) {
        console.error('[PIK Mandatory] Sync failed:', err);
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        // Reload as fallback to get correct state
        if (window.theme && window.theme.pageType === 'cart') {
          window.location.reload();
        }
      });
  }

  // ---------------------------------------------------------------------------
  // Main run loop
  // ---------------------------------------------------------------------------

  /**
   * Main function: fetch cart, build config map, apply UI changes.
   * Idempotent — safe to call multiple times.
   */
  function run() {
    if (running) return;
    running = true;

    fetchCart()
      .then(function (cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
          running = false;
          return;
        }

        var configMap = buildConfigMap(cart.items);

        // Check if there are any mandatory items at all
        var hasMandatory = false;
        for (var id in configMap) {
          if (configMap[id].mandatoryKey) {
            hasMandatory = true;
            break;
          }
        }

        if (!hasMandatory) {
          running = false;
          return;
        }

        var lineItemElements = findLineItemElements();
        if (lineItemElements.length === 0) {
          running = false;
          return;
        }

        applyHiding(configMap, lineItemElements);
        attachQuantityInterceptors(configMap, lineItemElements);

        running = false;
      })
      .catch(function (err) {
        console.error('[PIK Mandatory] Error:', err);
        running = false;
      });
  }

  /**
   * Debounced run — prevents rapid re-execution from multiple events.
   */
  function scheduleRun() {
    if (runTimeout) clearTimeout(runTimeout);
    runTimeout = setTimeout(run, 150);
  }

  // ---------------------------------------------------------------------------
  // Event listeners (Responsibility 4 — Section Swap Resilience)
  // ---------------------------------------------------------------------------

  // Shopify / Warehouse theme events
  document.addEventListener('cart:refresh', scheduleRun);
  document.addEventListener('cart:rerendered', scheduleRun);
  document.addEventListener('cart:updated', scheduleRun);
  document.addEventListener('theme:cart:refresh', scheduleRun);
  document.addEventListener('product:added', scheduleRun);

  // Custom event from Warehouse section rendering
  document.addEventListener('shopify:section:load', scheduleRun);

  // MutationObserver for dynamically rendered fragments (mini-cart drawer)
  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      var shouldRun = false;
      for (var i = 0; i < mutations.length; i++) {
        var target = mutations[i].target;
        if (!(target instanceof HTMLElement)) continue;
        // Only react to changes inside cart containers
        for (var c = 0; c < CART_CONTAINERS.length; c++) {
          if (target.matches(CART_CONTAINERS[c]) || target.closest(CART_CONTAINERS[c])) {
            shouldRun = true;
            break;
          }
        }
        if (shouldRun) break;
      }
      if (shouldRun) scheduleRun();
    });

    // Observe the whole body for subtree changes (cart drawer can be anywhere)
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        observer.observe(document.body, { childList: true, subtree: true });
      });
    }
  }

  // Initial run
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleRun);
  } else {
    scheduleRun();
  }
})();
